import React from 'react';
import { useParams } from 'react-router-dom';
import { 
  Download, 
  Printer, 
  Share2, 
  Clock,
  AlertCircle,
  Users,
  Eye,
  ThumbsUp,
  MessageSquare,
  FileText
} from 'lucide-react';
import { motion } from 'framer-motion';
import { sopData } from '../../data/sopData';
import BackButton from '../../../../components/BackButton';

const AnnouncementDetail = () => {
  const { id } = useParams();
  const announcement = sopData.announcements.find(a => a.id === id);

  if (!announcement) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
        <h2 className="text-xl font-semibold text-gray-900">Announcement Not Found</h2>
        <p className="text-gray-500 mt-2">The announcement you're looking for doesn't exist.</p>
        <BackButton label="Back to Announcements" />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="max-w-4xl mx-auto"
    >
      <div className="mb-8">
        <BackButton label="Back to Announcements" />
        
        <div className="flex justify-between items-start mt-6">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <span className={`px-3 py-1 text-sm font-medium rounded-full ${
                announcement.priority === 'high' ? 'bg-red-100 text-red-700' :
                announcement.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                'bg-blue-100 text-blue-700'
              }`}>
                {announcement.priority.charAt(0).toUpperCase() + announcement.priority.slice(1)} Priority
              </span>
              <span className="text-sm text-gray-500">
                Posted {announcement.date}
              </span>
            </div>
            <h1 className="text-3xl font-bold text-gray-900">{announcement.title}</h1>
            <p className="text-gray-500 mt-2">
              By {announcement.author} • {announcement.department}
            </p>
          </div>
          <div className="flex gap-3">
            <button className="p-2 hover:bg-gray-100 rounded-lg">
              <Share2 className="w-5 h-5 text-gray-600" />
            </button>
            <button className="p-2 hover:bg-gray-100 rounded-lg">
              <Printer className="w-5 h-5 text-gray-600" />
            </button>
            <button className="p-2 hover:bg-gray-100 rounded-lg">
              <Download className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
        <div className="prose max-w-none">
          {announcement.content}
        </div>
      </div>

      {/* Attachments */}
      {announcement.attachments && announcement.attachments.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Attachments</h2>
          <div className="space-y-3">
            {announcement.attachments.map((attachment, index) => (
              <div 
                key={index}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-blue-600" />
                  <span className="text-sm text-gray-700">{attachment.name}</span>
                </div>
                <button className="text-blue-600 hover:text-blue-700">
                  <Download className="w-5 h-5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Engagement Stats */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="grid grid-cols-3 gap-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 rounded-lg">
              <Eye className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Views</p>
              <p className="text-lg font-semibold text-gray-900">{announcement.views}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-50 rounded-lg">
              <ThumbsUp className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Acknowledgments</p>
              <p className="text-lg font-semibold text-gray-900">{announcement.acknowledgments}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-50 rounded-lg">
              <MessageSquare className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Comments</p>
              <p className="text-lg font-semibold text-gray-900">{announcement.comments}</p>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default AnnouncementDetail; 